import React, {Dispatch, FC, SetStateAction, useEffect, useState} from 'react';
import {Form, message, Modal} from "antd";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {defineMessages, useIntl} from "react-intl";
import {CommonButtonMessages} from "../../../../intl/commonMessages/CommonButtonMessages";
import {CommonFormMessages} from "../../../../intl/commonMessages/CommonFormMessages";
import {CustomModal} from "../../../../components/UI/AntCustom/CustomModal";
import {CustomFieldInput} from "../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomDatePicker} from "../../../../components/UI/AntCustom/CustomDatePicker";
import {IRegistration} from "../../../../store/reducers/registrationsReducer/registrationsReducerTypes";
import dayjs, {Dayjs} from "dayjs";
import {
  updateTrialEndDate,
  clearUpdateTrialEndDate,
} from "../../../../store/reducers/tariffReducer/tariffReducer";

const UpdateTrialEndDateModalMessages = defineMessages({
  title: {
    id: "updateTrialEndDateModal.title",
    defaultMessage: "Change trial end date",
  },
  description: {
    id: "updateTrialEndDateModal.description",
    defaultMessage: "Are you sure you want to change the trial end date?",
  },
  dateLabel: {
    id: "updateTrialEndDateModal.dateLabel",
    defaultMessage: "Trial end date",
  },
  datePlaceholder: {
    id: "updateTrialEndDateModal.datePlaceholder",
    defaultMessage: "Select trial end date",
  },
})

interface IUpdateTrialEndDateModal {
  user: IRegistration;
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  onSuccess?: () => void;
}

export const UpdateTrialEndDateModal: FC<IUpdateTrialEndDateModal> = ({
  user,
  isVisible,
  setIsVisible,
  onSuccess,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  
  const currentTariff = user.tariff?.companyTariffPackages?.[0];
  const currentTariffEnd = currentTariff?.tariffEnd;
  
  const [trialEndDate, setTrialEndDate] = useState<Dayjs | undefined>(
    currentTariffEnd ? dayjs(currentTariffEnd) : undefined
  );

  const {
    isLoading,
    isSuccess,
    errorMessage,
  } = useAppSelector(state => state.tariff.updateTrialEndDate);

  useEffect(() => {
    if (isVisible && currentTariffEnd) {
      setTrialEndDate(dayjs(currentTariffEnd));
      form.setFieldsValue({trialEndDate: dayjs(currentTariffEnd)});
    }
  }, [isVisible, currentTariffEnd, form]);

  useEffect(() => {
    if (!isLoading) {
      if (isSuccess) {
        setIsVisible(false);
        message.success(intl.formatMessage(CommonButtonMessages.save));
        if (onSuccess) {
          onSuccess();
        }
      }
      if (errorMessage) {
        message.error(errorMessage);
      }
    }

    return () => {
      dispatch(clearUpdateTrialEndDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, errorMessage, isLoading])

  const handleOk = () => {
    form.validateFields().then((values) => {
      const dateValue = values.trialEndDate;
      if (!dateValue) {
        message.error(intl.formatMessage(CommonFormMessages.ruleRequired));
        return;
      }

      // Запрос подтверждения действия перед сохранением
      Modal.confirm({
        title: intl.formatMessage(UpdateTrialEndDateModalMessages.title),
        content: intl.formatMessage(UpdateTrialEndDateModalMessages.description),
        okText: intl.formatMessage(CommonButtonMessages.save),
        cancelText: intl.formatMessage(CommonButtonMessages.cancel),
        onOk: () => {
          dispatch(updateTrialEndDate({
            userId: user.id,
            tariffEnd: dateValue.toISOString(),
          }));
        },
      });
    }).catch(() => {
      // Валидация не прошла
    });
  };

  return (
    <CustomModal
      open={isVisible}
      title={intl.formatMessage(UpdateTrialEndDateModalMessages.title)}
      centered
      okButtonProps={{loading: isLoading, type: "primary"}}
      okText={intl.formatMessage(CommonButtonMessages.save)}
      cancelText={intl.formatMessage(CommonButtonMessages.cancel)}
      onCancel={() => setIsVisible(false)}
      onOk={handleOk}
      afterClose={() => {
        dispatch(clearUpdateTrialEndDate());
        form.resetFields();
        setTrialEndDate(currentTariffEnd ? dayjs(currentTariffEnd) : undefined);
      }}
      width={500}
    >
      <Form form={form} layout="vertical">
        <CustomFieldInput
          label={intl.formatMessage(UpdateTrialEndDateModalMessages.dateLabel)}
          name={'trialEndDate'}
          rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
        >
          <CustomDatePicker
            placeholder={intl.formatMessage(UpdateTrialEndDateModalMessages.datePlaceholder)}
            style={{width: "100%"}}
            inputReadOnly={true}
            placement={"bottomLeft"}
            getPopupContainer={trigger => trigger.parentNode as HTMLElement}
            disabledDate={(current) => {
              if (!current) return false;
              return current && current < dayjs().startOf('day');
            }}
            value={trialEndDate}
            onChange={(date) => {
              setTrialEndDate(date || undefined);
              form.setFieldsValue({trialEndDate: date});
            }}
          />
        </CustomFieldInput>
      </Form>
    </CustomModal>
  );
};
